import { useDisclosure } from '@mantine/hooks';
import { Box, CircularProgress } from '@mui/material';
import { useGetInstance } from 'gen/client';
import mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import useSWRImmutable from 'swr/immutable';
import 'mapbox-gl/dist/mapbox-gl.css';

import { GeobufFetcher } from '@ui/Utils/geobuf/GeobufFetcher';

import instanceTypes from 'kerberus-components/src/Data/Instance/instanceTypes';

import BaseMap from './BaseMap';
import InstanceMapControls from './InstanceMapControls';
import InstanceMapModal from './InstanceMapModal';
import {
    DEFAULT_INSTANCE_ZOOM,
    LOCATIONS_CLUSTER,
    LOCATIONS_CLUSTER_COUNT,
    LOCATIONS_LAYER_JOINED,
    LOCATIONS_LAYER_NOT_JOINED,
    LOCATIONS_SOURCE,
    defaultCoords,
} from './mapConstants';
import { buildInstancePopupHTML } from './mapPopup';
import { InstanceFeatureProperties, isFeatureCollection } from './mapTypes';
import { toLngLatTuple } from './mapUtils';
import useInstanceMeta from './useInstanceMeta';

import type { MapMouseEvent, GeolocateControl as MapboxGeolocateControl } from 'mapbox-gl';
import type { MapRef } from 'react-map-gl/mapbox';

interface ReactMapProps {
    handleClose?: () => void;
    instanceId?: string;
}

export default function ReactMap({ handleClose, instanceId }: Readonly<ReactMapProps>) {
    const { data: locations = [] } = useSWRImmutable('instances/locations', GeobufFetcher);
    const locationsData: GeoJSON.GeoJSON = locations as unknown as GeoJSON.FeatureCollection;

    const [mapReady, setMapReady] = useState<boolean>(false);
    const [forceShowGeolocate, setForceShowGeolocate] = useState<boolean>(false);
    const [geolocateReady, setGeolocateReady] = useState<boolean>(false);
    const [initialViewReady, setInitialViewReady] = useState<boolean>(!!instanceId);
    const [geoSettled, setGeoSettled] = useState<boolean>(false);

    const mapRef = useRef<MapRef | null>(null);
    const popupRef = useRef<mapboxgl.Popup | null>(null);
    const geolocateCtrlRef = useRef<MapboxGeolocateControl | null>(null);

    // Retry/settle timers for geolocation
    const geoRetryTimerRef = useRef<number | undefined>(undefined);
    const geoSettleTimerRef = useRef<number | undefined>(undefined);

    // Persist last known user coords to render our own marker
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userLngLat, setUserLngLat] = useState<[number, number] | undefined>(undefined);
    const { descriptions, ensureDescription, joinedMeta, joinedSet } = useInstanceMeta();

    const [showOnlyCurrent, setShowOnlyCurrent] = useState<boolean>(!!instanceId);
    const [cursor, setCursor] = useState<string>('auto');
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    useEffect(() => {
        setShowOnlyCurrent(!!instanceId);
    }, [instanceId]);

    const onlyCurrentEffective = useMemo(() => {
        if (!instanceId) {
            return false;
        }
        return showOnlyCurrent;
    }, [instanceId, showOnlyCurrent]);

    const interactionsEnabled = useMemo(() => !instanceId || isFullscreen, [instanceId, isFullscreen]);

    // Keep default cursor when interactions are disabled
    const mapCursor = useMemo(() => (interactionsEnabled ? cursor : 'auto'), [interactionsEnabled, cursor]);

    // Memoized arrays to reduce re-creation
    const interactiveLayerIds = useMemo(
        () => [LOCATIONS_LAYER_JOINED, LOCATIONS_LAYER_NOT_JOINED, LOCATIONS_CLUSTER],
        [],
    );

    // Helper to validate [lng, lat] arrays
    const isValidLngLat = useCallback((coords: unknown): coords is [number, number] => {
        return (
            Array.isArray(coords) &&
            coords.length >= 2 &&
            typeof coords[0] === 'number' &&
            typeof coords[1] === 'number' &&
            Number.isFinite(coords[0]) &&
            Number.isFinite(coords[1])
        );
    }, []);

    // Filter features that have valid Point coordinates
    const pointFeatures = useMemo(() => {
        if (!isFeatureCollection(locationsData)) {
            return [];
        }
        return locationsData.features.filter((f): f is GeoJSON.Feature<GeoJSON.Point, InstanceFeatureProperties> => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!f.geometry || f.geometry.type !== 'Point') {
                return false;
            }
            const coords = f.geometry.coordinates;
            return isValidLngLat(coords);
        });
    }, [locationsData, isValidLngLat]);

    // Track entering/leaving fullscreen from the FullscreenControl
    useEffect(() => {
        const handleFsChange = () => {
            const fsEl = document.fullscreenElement;
            if (!fsEl) {
                setIsFullscreen(false);
                return;
            }
            const map = mapRef.current?.getMap();
            const container = map && 'getContainer' in map ? (map.getContainer() as Element) : undefined;
            // If the fullscreen element is the map container or contains it, consider fullscreen
            const isFs = container ? fsEl === container || fsEl.contains(container) || container.contains(fsEl) : true;
            setIsFullscreen(isFs);
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        // initialize on mount in case already fullscreen
        handleFsChange();
        return () => document.removeEventListener('fullscreenchange', handleFsChange);
    }, []);

    const currentInstanceCoords = useMemo((): [number, number] | undefined => {
        if (!instanceId) {
            return undefined;
        }
        const found = pointFeatures.find((feature) => {
            const props = feature.properties as InstanceFeatureProperties | undefined;
            const featureId = props?.id ?? feature.id;
            return featureId != null && String(featureId) === String(instanceId);
        });
        return found?.geometry.coordinates as [number, number] | undefined;
    }, [pointFeatures, instanceId]);

    // Keep latest coords in a ref for fullscreenchange handler
    const currentInstanceCoordsRef = useRef<[number, number] | undefined>(undefined);
    useEffect(() => {
        currentInstanceCoordsRef.current = currentInstanceCoords;
    }, [currentInstanceCoords]);

    const initialCenter = useMemo<[number, number]>(() => {
        if (currentInstanceCoords) {
            return currentInstanceCoords;
        }
        const dc = toLngLatTuple(defaultCoords);
        return dc ?? [0, 0];
    }, [currentInstanceCoords]);

    // When leaving fullscreen, recenter to current instance if available
    const wasFullscreenRef = useRef<boolean>(false);
    useEffect(() => {
        if (wasFullscreenRef.current && !isFullscreen) {
            const map = mapRef.current?.getMap();
            const coords = currentInstanceCoordsRef.current;
            if (map && coords) {
                const nextZoom = Math.max(map.getZoom(), DEFAULT_INSTANCE_ZOOM);
                map.easeTo({ center: coords, zoom: nextZoom });
            }
        }
        wasFullscreenRef.current = isFullscreen;
    }, [isFullscreen]);

    const [selectedInstance, setSelectedInstance] = useState<string>();
    const { data: instance } = useGetInstance(selectedInstance ?? '', {
        swr: { enabled: !!selectedInstance && !instanceId },
    });
    const [instanceModalOpen, { close: handleInstanceModalClose, open: handleInstanceModalOpen }] =
        useDisclosure(false);

    const enrichedData = useMemo<GeoJSON.GeoJSON>(() => {
        const data = locationsData;
        if ('features' in data && Array.isArray(data.features)) {
            const fc = data;
            const baseFeatures =
                showOnlyCurrent && instanceId
                    ? fc.features.filter((feature) => {
                          const featureId =
                              (feature.properties as InstanceFeatureProperties | undefined)?.id ?? feature.id;
                          return featureId != null && String(featureId) === String(instanceId);
                      })
                    : fc.features;

            // Drop features without valid Point coordinates to avoid mapbox errors
            const filteredBase = baseFeatures.filter(
                (feature): feature is GeoJSON.Feature<GeoJSON.Point, InstanceFeatureProperties> => {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    if (!feature.geometry || feature.geometry.type !== 'Point') {
                        return false;
                    }
                    const coords = feature.geometry.coordinates;
                    return isValidLngLat(coords);
                },
            );

            return {
                features: filteredBase.map((feature) => {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    const properties: InstanceFeatureProperties = { ...(feature.properties || {}) };
                    const rawId = properties.id ?? feature.id;
                    let idStr: string | undefined;
                    if (typeof rawId === 'number') {
                        idStr = String(rawId);
                    } else if (typeof rawId === 'string') {
                        idStr = rawId;
                    }
                    if (idStr) {
                        properties.joined = joinedSet.has(idStr);
                    }
                    const featureId: string | number | undefined =
                        typeof rawId === 'number' || typeof rawId === 'string' ? rawId : idStr;
                    return { ...feature, id: featureId, properties } as GeoJSON.Feature;
                }),
                type: 'FeatureCollection',
            } as GeoJSON.FeatureCollection;
        }
        return data;
    }, [locationsData, showOnlyCurrent, instanceId, joinedSet, isValidLngLat]);

    // recentering effects
    const recenteredKeyRef = useRef<string>('');
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !currentInstanceCoords) {
            return;
        }
        const coordKey = Array.isArray(currentInstanceCoords)
            ? currentInstanceCoords.join(',')
            : JSON.stringify(currentInstanceCoords);
        const key = `${instanceId ?? ''}:${coordKey}`;
        if (recenteredKeyRef.current === key) {
            return;
        }

        const nextZoom = Math.max(map.getZoom(), DEFAULT_INSTANCE_ZOOM);
        map.easeTo({ center: currentInstanceCoords, zoom: nextZoom });
        recenteredKeyRef.current = key;
    }, [currentInstanceCoords, instanceId]);

    // cluster expansion on click
    const handleMapClick = useCallback(
        (e: MapMouseEvent) => {
            if (!interactionsEnabled) {
                return;
            }
            const map = mapRef.current?.getMap();
            if (!map) {
                return;
            }
            const features = e.features;
            if (!features || features.length === 0) {
                return;
            }
            const feat = features[0];
            if (!feat) {
                return;
            }
            const layerId = feat.layer && 'id' in feat.layer ? (feat.layer.id as string | undefined) : undefined;

            if (layerId === LOCATIONS_CLUSTER) {
                const clusterId = (feat.properties as { cluster_id?: number } | undefined)?.cluster_id;
                if (typeof clusterId !== 'number') {
                    return;
                }
                const src = map.getSource(LOCATIONS_SOURCE);
                if (src && 'getClusterExpansionZoom' in src) {
                    src.getClusterExpansionZoom(clusterId, (err, zoom) => {
                        if (err || typeof zoom !== 'number') {
                            return;
                        }
                        if (feat.geometry.type !== 'Point') {
                            return;
                        }
                        const coordsRaw = feat.geometry.coordinates;
                        if (!isValidLngLat(coordsRaw)) {
                            return;
                        }
                        const coords = coordsRaw;
                        map.easeTo({ center: coords, zoom });
                    });
                }
                return;
            }

            if (layerId === LOCATIONS_LAYER_JOINED || layerId === LOCATIONS_LAYER_NOT_JOINED) {
                // Do not open InstanceMapModal when instanceId is set (to avoid multiple modals)
                if (instanceId) {
                    return;
                }
                const props = feat.properties as InstanceFeatureProperties | undefined;
                const id = props?.id ?? feat.id;
                if (typeof id === 'string' || typeof id === 'number') {
                    setSelectedInstance(String(id));
                    handleInstanceModalOpen();
                }
            }
        },
        [interactionsEnabled, handleInstanceModalOpen, isValidLngLat, instanceId],
    );

    // hover popup using mapboxgl.Popup to reuse the HTML builder
    const handleMouseMove = useCallback(
        (e: MapMouseEvent) => {
            const map = mapRef.current?.getMap();
            if (!map || !interactionsEnabled) {
                return;
            }
            const features = e.features;
            if (!features || features.length === 0) {
                popupRef.current?.remove();
                return;
            }
            const feat = features[0];
            if (!feat) {
                popupRef.current?.remove();
                return;
            }
            const isCluster = Boolean((feat.properties as Record<string, unknown> | undefined)?.cluster);
            if (isCluster) {
                popupRef.current?.remove();
                return;
            }
            if (feat.geometry.type !== 'Point') {
                return;
            }
            const coordsRaw = feat.geometry.coordinates;
            if (!isValidLngLat(coordsRaw)) {
                popupRef.current?.remove();
                return;
            }
            const coords = coordsRaw;
            const idVal = (feat.properties && (feat.properties as Record<string, unknown>).id) as
                | string
                | number
                | undefined;
            const idStr = idVal === undefined ? '' : String(idVal);
            const joinedVal = (feat.properties && (feat.properties as Record<string, unknown>).joined) as
                | boolean
                | undefined;

            if (idStr) {
                ensureDescription(idStr);
            }
            const descr = descriptions[idStr];
            const meta = joinedMeta[idStr];
            const typeLabel = descr?.instance_type
                ? instanceTypes.find((i) => i.value === (descr.instance_type as string))?.label || descr.instance_type
                : undefined;

            const html = buildInstancePopupHTML({
                address: descr?.address,
                image: descr?.image,
                joined: joinedVal === true ? true : joinedVal === false ? false : undefined,
                loading: !descr,
                name: descr?.name || meta?.name || `Instance ${idStr}`,
                typeLabel,
            });

            if (!popupRef.current) {
                popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
            }
            popupRef.current.setLngLat(coords).setHTML(html).addTo(map);
        },
        [descriptions, ensureDescription, interactionsEnabled, joinedMeta, isValidLngLat],
    );

    const handleMouseEnter = useCallback(() => {
        if (!interactionsEnabled) {
            return;
        }
        setCursor('pointer');
    }, [interactionsEnabled]);

    const handleMouseLeave = useCallback(() => {
        if (!interactionsEnabled) {
            return;
        }
        setCursor('auto');
        const map = mapRef.current?.getMap();
        if (map) {
            map.getCanvas().style.cursor = '';
        }
        popupRef.current?.remove();
    }, [interactionsEnabled]);

    // Fit all instances on button click and center the map
    const handleFitAllInstances = useCallback(() => {
        const map = mapRef.current?.getMap();
        if (!map) {
            return;
        }
        if (pointFeatures.length === 0) {
            return;
        }
        const bounds = new mapboxgl.LngLatBounds();
        for (const feature of pointFeatures) {
            bounds.extend(feature.geometry.coordinates as [number, number]);
        }
        map.fitBounds(bounds, { duration: 500, maxZoom: 13, padding: 40 });
    }, [pointFeatures]);

    // Fit current instance on button click and center the map
    function handleFitCurrentInstance() {
        if (!instanceId) {
            return;
        }
        const map = mapRef.current?.getMap();
        if (!map) {
            return;
        }

        const feature = pointFeatures.find((f) => {
            const props = f.properties as InstanceFeatureProperties | undefined;
            const fid = props?.id ?? f.id;
            return fid != null && String(fid) === String(instanceId);
        });

        if (!feature) {
            return;
        }

        const coords = feature.geometry.coordinates as [number, number];
        map.flyTo({ center: coords, duration: 500, zoom: DEFAULT_INSTANCE_ZOOM });
    }

    // Toggle: show all ↔ only current, and fit accordingly
    function handleToggleShowOnlyCurrent() {
        if (!instanceId) {
            return;
        }

        if (onlyCurrentEffective) {
            // Was showing only current -> switch to all, then fit all
            setShowOnlyCurrent(false);
            handleFitAllInstances();
        } else {
            // Was showing all -> switch to only current, then fit that one
            setShowOnlyCurrent(true);
            handleFitCurrentInstance();
        }
    }

    // ensure popup removed when interactions disabled
    useEffect(() => {
        if (!interactionsEnabled) {
            popupRef.current?.remove();
        }
    }, [interactionsEnabled]);

    useEffect(() => {
        if (instanceId) {
            return;
        }
        if (!mapReady) {
            return;
        }
        if (initialViewReady) {
            return;
        }
        if (!geoSettled) {
            return;
        }
        setInitialViewReady(true);
    }, [instanceId, mapReady, geoSettled, initialViewReady]);

    // Subscribe to GeolocateControl to capture user position and settle geolocation
    useEffect(() => {
        if (instanceId) {
            return;
        }
        const map = mapRef.current?.getMap();
        const ctrl = geolocateCtrlRef.current;
        if (!map || !ctrl || !geolocateReady) {
            return;
        }

        interface GeolocateCtrlEvents {
            off?: (type: 'geolocate' | 'error', fn: (e: unknown) => void) => void;
            on: (type: 'geolocate' | 'error', fn: (e: unknown) => void) => void;
        }
        const events = ctrl as unknown as GeolocateCtrlEvents;

        const clearGeoTimers = () => {
            if (geoRetryTimerRef.current) {
                window.clearTimeout(geoRetryTimerRef.current);
                geoRetryTimerRef.current = undefined;
            }
            if (geoSettleTimerRef.current) {
                window.clearTimeout(geoSettleTimerRef.current);
                geoSettleTimerRef.current = undefined;
            }
        };

        const onGeolocate = (evt: unknown) => {
            const pos = evt as GeolocationPosition;
            setUserLngLat([pos.coords.longitude, pos.coords.latitude]);
            clearGeoTimers();
            setGeoSettled(true);
        };

        const onError = () => {
            clearGeoTimers();
            setGeoSettled(true);
        };

        try {
            events.on('geolocate', onGeolocate);
            events.on('error', onError);
        } catch {
            // ignore
        }

        return () => {
            try {
                events.off?.('geolocate', onGeolocate);
                events.off?.('error', onError);
            } catch {
                // ignore
            }
        };
    }, [instanceId, geolocateReady]);

    // Trigger geolocation once and do a single retry before settling
    useEffect(() => {
        if (instanceId || !mapReady || !geolocateReady) {
            return;
        }
        setGeoSettled(false);
        const ctrl = geolocateCtrlRef.current;
        try {
            ctrl?.trigger();
        } catch {
            // ignore
        }
        geoRetryTimerRef.current = window.setTimeout(() => {
            try {
                ctrl?.trigger();
            } catch {
                // ignore
            }
        }, 600);
        geoSettleTimerRef.current = window.setTimeout(() => {
            setGeoSettled(true);
        }, 2000);
        return () => {
            if (geoRetryTimerRef.current) {
                window.clearTimeout(geoRetryTimerRef.current);
            }
            if (geoSettleTimerRef.current) {
                window.clearTimeout(geoSettleTimerRef.current);
            }
            geoRetryTimerRef.current = undefined;
            geoSettleTimerRef.current = undefined;
        };
    }, [instanceId, mapReady, geolocateReady]);

    // After the first open of the map without an instance, always render GeolocateControl (in-memory only)
    useEffect(() => {
        if (!forceShowGeolocate && mapReady && !instanceId) {
            setForceShowGeolocate(true);
        }
    }, [mapReady, instanceId, forceShowGeolocate]);

    return (
        <>
            {/* Do not render InstanceMapModal when instanceId is provided to avoid multiple modals */}
            {!instanceId && instance ? (
                <InstanceMapModal handleClose={handleInstanceModalClose} instance={instance} open={instanceModalOpen} />
            ) : null}

            <Box
                sx={{
                    backgroundColor: 'transparent',
                    height: '100%',
                    inset: 'auto',
                    position: 'relative',
                    width: '100%',
                    zIndex: 'auto',
                }}
            >
                {/* Loading overlay for no-instance initial viewport preparation */}
                {!instanceId && !initialViewReady ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            backgroundColor: 'background.default',
                            bottom: 0,
                            display: 'flex',
                            inset: 0,
                            justifyContent: 'center',
                            left: 0,
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <CircularProgress size={28} />
                    </Box>
                ) : null}
                <BaseMap
                    center={initialCenter}
                    cursor={mapCursor}
                    interactive={interactionsEnabled}
                    interactiveLayerIds={interactiveLayerIds}
                    mapRef={mapRef as React.Ref<MapRef>}
                    onClick={handleMapClick}
                    onLoad={() => setMapReady(true)}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                    zoom={DEFAULT_INSTANCE_ZOOM}
                >
                    {/* GeoJSON source with clustering */}
                    {isFeatureCollection(enrichedData) ? (
                        <Source cluster data={enrichedData} id={LOCATIONS_SOURCE} type="geojson">
                            <Layer
                                filter={['all', ['!=', ['get', 'cluster'], true], ['==', ['get', 'joined'], true]]}
                                id={LOCATIONS_LAYER_JOINED}
                                paint={{
                                    'circle-color': '#1C4E71',
                                    'circle-radius': 10,
                                    'circle-stroke-color': '#ffffff',
                                    'circle-stroke-width': 2,
                                }}
                                type="circle"
                            />
                            <Layer
                                filter={['all', ['!=', ['get', 'cluster'], true], ['!=', ['get', 'joined'], true]]}
                                id={LOCATIONS_LAYER_NOT_JOINED}
                                paint={{
                                    'circle-color': '#9e9e9e',
                                    'circle-radius': 10,
                                    'circle-stroke-color': '#ffffff',
                                    'circle-stroke-width': 1.5,
                                }}
                                type="circle"
                            />
                            <Layer
                                filter={['has', 'point_count']}
                                id={LOCATIONS_CLUSTER}
                                paint={{
                                    'circle-color': '#1C4E71',
                                    'circle-opacity': 0.75,
                                    'circle-radius': ['step', ['get', 'point_count'], 20, 25, 26, 100, 34],
                                }}
                                type="circle"
                            />
                            <Layer
                                filter={['has', 'point_count']}
                                id={LOCATIONS_CLUSTER_COUNT}
                                layout={{
                                    'text-field': ['get', 'point_count_abbreviated'],
                                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                                    'text-size': 16,
                                }}
                                paint={{ 'text-color': '#ffffff' }}
                                type="symbol"
                            />
                        </Source>
                    ) : null}

                    {/* {userLngLat ? (
                        <Marker anchor="center" latitude={userLngLat[1]} longitude={userLngLat[0]}>
                            <FmdGoodIcon color="primary" sx={{ fontSize: 22 }} />
                        </Marker>
                    ) : null} */}
                    <InstanceMapControls
                        forceShowGeolocate={forceShowGeolocate}
                        geolocateRef={(ctrl) => {
                            geolocateCtrlRef.current = ctrl;
                            setGeolocateReady(Boolean(ctrl));
                        }}
                        hasInstanceId={!!instanceId}
                        isFullscreen={isFullscreen}
                        moreThanOneInstance={pointFeatures.length > 1}
                        onClose={handleClose}
                        onFitAllInstances={handleFitAllInstances}
                        onlyCurrentEffective={onlyCurrentEffective}
                        onToggleShowOnlyCurrent={handleToggleShowOnlyCurrent}
                    />
                </BaseMap>
            </Box>
        </>
    );
}
